import BaseButton from 'base-components/BaseButton/BaseButton';
import BaseModal from 'base-components/BaseModal/BaseModal';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {  ModalFooter, ModalHeader, ModalBody } from 'reactstrap';
import BaseForm from 'base-components/BaseForm/BaseForm';
import BaseValidatedInput from 'base-components/BaseValidatedInput/BaseValidatedInput';
import BaseValidatedDropDownList from 'base-components/BaseValidatedDropDownList/BaseValidatedDropDownList';
import 'scss/Main.scss';
import { DefaultValues, FieldValues } from 'react-hook-form';
import BaseDatePicker from 'base-components/BaseDatePicker/BaseDatePicker';
import { defaultDatetimeFormat, PLATFORMS } from 'utils/consts';
import { formatToDateTimeString } from 'utils/formatters';

type MaintenanceChangeModalProps<T> = {
    isOpen?: boolean;
    toggle: Dispatch<SetStateAction<boolean>>;
    employeeId?: number;
    isEditing?: boolean;
    handleChange: (form: T) => void;
    editableRow?: DefaultValues<T>,
    defaultValuesProps: DefaultValues<T>,
};

const MaintenanceEditModal = <T extends FieldValues,>({ isOpen = false, toggle, editableRow, handleChange, isEditing, defaultValuesProps }: MaintenanceChangeModalProps<T>) => {
    const defaultValues = editableRow ?? defaultValuesProps;

    const handleSubmit = (form: T) => {
        const dates = {
            maintenanceBegin: formatToDateTimeString(new Date(maintenanceBegin as string)),
            maintenanceEnd: formatToDateTimeString(new Date(maintenanceEnd as string)),
        }

        const body = isEditing ? {
            id: form.id,
            reason: form.reason,
            ...dates,
        } : {
            ...form,
            ...dates,
        }

        handleChange(body as T)

        toggle(false)
    }
    
    useEffect(() => {
        if (editableRow) {
            setMaintenanceBegin(editableRow.maintenanceBegin)
            
            setMaintenanceEnd(editableRow.maintenanceEnd)
        }
    }, [editableRow])

    const [maintenanceBegin, setMaintenanceBegin] = useState<string | null>(null);
    const [maintenanceEnd, setMaintenanceEnd] = useState<string | null>(null);

    return (
        <BaseModal
            size="xl"
            isOpen={isOpen}
            toggle={() => toggle(false)}
            scrollable
            className="clients__modal"
        >
            <BaseForm<T>
                onSubmit={handleSubmit}
                defaultValues={defaultValues}
            >
                <ModalHeader toggle={() => toggle(false)}>
                    {isEditing ? 'Редактирование ТО' : 'Планирование ТО'}
                </ModalHeader>
                <ModalBody className="custom-scrollbar px-8 pt-8 pb-0">
                    <div className='row'>
                        <div className='col'>
                            <BaseValidatedInput
                                label="Причина"
                                className="mx-1"
                                name="reason"
                                isFloating={true}
                            />
                        </div>

                        <div className='col'>
                            <BaseValidatedDropDownList
                                label="Платформа"
                                name="platform"
                                items={PLATFORMS}
                                disabled={isEditing}
                            />
                        </div>
                    </div>
                    <div className='row'>
                        <div className='col'>
                            <BaseDatePicker
                                displayDateFormat={defaultDatetimeFormat}
                                value={defaultValues.maintenanceBegin}
                                inputProps={{
                                    label: 'Дата начала',
                                    name: 'maintenanceBegin',
                                    loading: false,
                                }}
                                minDate={new Date()}
                                onChange={setMaintenanceBegin}
                            />
                        </div>
                        <div className='col'>
                            <BaseDatePicker
                                displayDateFormat={defaultDatetimeFormat}
                                value={defaultValues.maintenanceEnd}
                                inputProps={{
                                    label: 'Дата окончания',
                                    name: 'maintenanceEnd',
                                    loading: false,
                                }}
                                minDate={new Date()}
                                onChange={setMaintenanceEnd}
                            />
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter className='px-8'>
                    <BaseButton
                        type='submit'
                        color="success"
                        className={maintenanceBegin && maintenanceEnd ? '' : 'disabled'} >
                        {isEditing ? 'Сохранить' : 'Запланировать ТО'}
                    </BaseButton>
                    <BaseButton
                        color="secondary"
                        onClick={() => toggle(false)}>
                        Закрыть
                    </BaseButton>
                </ModalFooter>
            </BaseForm>
        </BaseModal>
    );
};

export default MaintenanceEditModal;
